import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { action } from "typesafe-actions";
import { api } from "../../api/api";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { IStoreState } from "../initialStoreState";
import {
  convertLoaderCompleted,
  convertLoaderProgress,
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { IGenAiPrompt, IGenAiContext } from "./genAi.types";

// ============ GenAI Prompt Actions ============

export const FETCH_GENAI_PROMPT_LIST_PROGRESS =
  "FETCH_GENAI_PROMPT_LIST_PROGRESS";
export const FETCH_GENAI_PROMPT_LIST_SUCCESS =
  "FETCH_GENAI_PROMPT_LIST_SUCCESS";
export const FETCH_GENAI_PROMPT_LIST_FAILED = "FETCH_GENAI_PROMPT_LIST_FAILED";

export const fetchGenAiPromptListProgress = () =>
  action(FETCH_GENAI_PROMPT_LIST_PROGRESS);
export const fetchGenAiPromptListSuccess = (
  data: IGenAiPrompt[],
  totalRecords: number,
  currentRecords: number,
) =>
  action(FETCH_GENAI_PROMPT_LIST_SUCCESS, {
    data,
    totalRecords,
    currentRecords,
  });
export const fetchGenAiPromptListFailed = () =>
  action(FETCH_GENAI_PROMPT_LIST_FAILED);

export const fetchGenAiPromptListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    date: IDataTableV2DateState["dates"],
    searchValue: IDatatableV2AdvancedSearchFilter,
    company_outlet_uuid?: string,
    status?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchGenAiPromptListProgress());
      // dispatch(saveLoaderProgress());

      let url = `/genai/get-genai-prompt?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}`;

      if (company_outlet_uuid) {
        url += `&company_outlet_uuid=${company_outlet_uuid}`;
      }

      if (searchValue.length > 0) {
        url += `&advanceFilter=${JSON.stringify(searchValue)}`;
      }

      if (status) {
        url += `&status=${status}`;
      }

      const finalUrl = url;

      // Make the API call
      const response = await api.get(finalUrl);

      // Check if the response is valid
      if (response.status === 200 && response.data) {
        const data: IGenAiPrompt[] = response.data.data;

        dispatch(
          fetchGenAiPromptListSuccess(
            data,
            response.data.totalRecords,
            response.data.currentRecords,
          ),
        );
      }
    } catch (err: any) {
      dispatch(fetchGenAiPromptListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to fetch GenAI prompts",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_GENAI_PROMPT_PROGRESS = "FETCH_GENAI_PROMPT_PROGRESS";
export const FETCH_GENAI_PROMPT_SUCCESS = "FETCH_GENAI_PROMPT_SUCCESS";
export const FETCH_GENAI_PROMPT_FAILED = "FETCH_GENAI_PROMPT_FAILED";

export const fetchGenAiPromptProgress = () =>
  action(FETCH_GENAI_PROMPT_PROGRESS);
export const fetchGenAiPromptSuccess = (data: IGenAiPrompt) =>
  action(FETCH_GENAI_PROMPT_SUCCESS, { data });
export const fetchGenAiPromptFailed = (errorMessage: string) =>
  action(FETCH_GENAI_PROMPT_FAILED, { errorMessage });

export const fetchGenAiPromptAsync =
  (promptId: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchGenAiPromptProgress());
      dispatch(saveLoaderProgress());

      let finalUrl = `/genai/get-genai-prompt?pageNo=1&itemPerPage=10&genai_prompt_uuid=${promptId}`;

      const res = await api.get(finalUrl);
      const data: IGenAiPrompt[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchGenAiPromptSuccess(data[0]));
      } else {
        dispatch(
          fetchGenAiPromptFailed(
            "Oops! We couldn't find any records associated with your GenAI prompt at the moment. Please ensure that the provided information is accurate and try again.",
          ),
        );
      }
    } catch (err: any) {
      dispatch(fetchGenAiPromptFailed(""));
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to fetch GenAI prompt",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertGenAiPromptAsync =
  (
    existingPrompt: IGenAiPrompt,
    onCallBack: (isSuccess?: boolean, data?: IGenAiPrompt) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(saveLoaderProgress());
    try {
      const { ...rest } = existingPrompt;

      const res = await api.post("/genai/upsert-genai-prompt", rest);
      dispatch(
        showMessage({
          type: "success",
          message: res.data.message,
          displayAs: "snackbar",
        }),
      );

      const data = res.data.data;
      onCallBack(true, data);
    } catch (err: any) {
      onCallBack(false);

      // Handle different error response formats
      let errorMessage = "An error occurred";
      if (err.response && err.response.data) {
        // For subscription limit errors (403 message in 401 response)
        if (err.response.data.message === 403 && err.response.data.data) {
          errorMessage = err.response.data.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        }
      }

      dispatch(
        showMessage({
          type: "error",
          message: errorMessage,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const CLEAR_GENAI_PROMPT_STATE = "CLEAR_GENAI_PROMPT_STATE";
export const CLEAR_GENAI_PROMPT = "CLEAR_GENAI_PROMPT";

export const clearGenAiPromptState = () => action(CLEAR_GENAI_PROMPT_STATE);
export const clearGenAiPrompt = () => action(CLEAR_GENAI_PROMPT);

// ============ GenAI Context Actions ============

export const FETCH_GENAI_CONTEXT_LIST_PROGRESS =
  "FETCH_GENAI_CONTEXT_LIST_PROGRESS";
export const FETCH_GENAI_CONTEXT_LIST_SUCCESS =
  "FETCH_GENAI_CONTEXT_LIST_SUCCESS";
export const FETCH_GENAI_CONTEXT_LIST_FAILED =
  "FETCH_GENAI_CONTEXT_LIST_FAILED";

export const fetchGenAiContextListProgress = () =>
  action(FETCH_GENAI_CONTEXT_LIST_PROGRESS);
export const fetchGenAiContextListSuccess = (
  data: IGenAiContext[],
  totalRecords: number,
  currentRecords: number,
) =>
  action(FETCH_GENAI_CONTEXT_LIST_SUCCESS, {
    data,
    totalRecords,
    currentRecords,
  });
export const fetchGenAiContextListFailed = () =>
  action(FETCH_GENAI_CONTEXT_LIST_FAILED);

export const fetchGenAiContextListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    searchValue?: IDatatableV2AdvancedSearchFilter | null,
    company_outlet_uuid?: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchGenAiContextListProgress());
      dispatch(saveLoaderProgress());

      let url = `/genai/get-genai-context?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&status=ACTIVE`;

      if (company_outlet_uuid) {
        url += `&company_outlet_uuid=${company_outlet_uuid}`;
      }

      if (searchValue && searchValue.length > 0) {
        url += `&advanceFilter=${JSON.stringify(searchValue)}`;
      }

      const finalUrl = url;

      // Make the API call
      const response = await api.get(finalUrl);

      // Check if the response is valid
      if (response.status === 200 && response.data) {
        const data: IGenAiContext[] = response.data.data;

        dispatch(
          fetchGenAiContextListSuccess(
            data,
            response.data.totalRecords,
            response.data.currentRecords,
          ),
        );
      }
    } catch (err: any) {
      dispatch(fetchGenAiContextListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to fetch GenAI contexts",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_GENAI_CONTEXT_PROGRESS = "FETCH_GENAI_CONTEXT_PROGRESS";
export const FETCH_GENAI_CONTEXT_SUCCESS = "FETCH_GENAI_CONTEXT_SUCCESS";
export const FETCH_GENAI_CONTEXT_FAILED = "FETCH_GENAI_CONTEXT_FAILED";

export const fetchGenAiContextProgress = () =>
  action(FETCH_GENAI_CONTEXT_PROGRESS);
export const fetchGenAiContextSuccess = (data: IGenAiContext) =>
  action(FETCH_GENAI_CONTEXT_SUCCESS, { data });
export const fetchGenAiContextFailed = (errorMessage: string) =>
  action(FETCH_GENAI_CONTEXT_FAILED, { errorMessage });

export const fetchGenAiContextAsync =
  (contextId: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchGenAiContextProgress());
      dispatch(saveLoaderProgress());

      let finalUrl = `/genai/get-genai-context?pageNo=1&itemPerPage=10&genai_context_uuid=${contextId}`;

      const res = await api.get(finalUrl);
      const data: IGenAiContext[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchGenAiContextSuccess(data[0]));
      } else {
        dispatch(
          fetchGenAiContextFailed(
            "Oops! We couldn't find any records associated with your GenAI context at the moment. Please ensure that the provided information is accurate and try again.",
          ),
        );
      }
    } catch (err: any) {
      dispatch(fetchGenAiContextFailed(""));
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to fetch GenAI context",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertGenAiContextAsync =
  (
    existingContext: IGenAiContext,
    onCallBack: (isSuccess?: boolean, data?: IGenAiContext) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(saveLoaderProgress());
    try {
      const { create_ts, insert_ts, ...rest } = existingContext;

      const res = await api.post("/genai/upsert-genai-context", rest);
      dispatch(
        showMessage({
          type: "success",
          message: res.data.message,
          displayAs: "snackbar",
        }),
      );

      const data = res.data.data;
      onCallBack(true, data);
    } catch (err: any) {
      onCallBack(false);

      // Handle different error response formats
      let errorMessage = "An error occurred";
      if (err.response && err.response.data) {
        // For subscription limit errors (403 message in 401 response)
        if (err.response.data.message === 403 && err.response.data.data) {
          errorMessage = err.response.data.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        }
      }

      dispatch(
        showMessage({
          type: "error",
          message: errorMessage,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const CLEAR_GENAI_CONTEXT_STATE = "CLEAR_GENAI_CONTEXT_STATE";
export const CLEAR_GENAI_CONTEXT = "CLEAR_GENAI_CONTEXT";

export const clearGenAiContextState = () => action(CLEAR_GENAI_CONTEXT_STATE);
export const clearGenAiContext = () => action(CLEAR_GENAI_CONTEXT);
